import { useState, useEffect, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getAllBuybackListings, setBuybackProductName, scanBuybackProductNames, getKnownBuybackProducts, updateBuybackListing, getBuybackTags, createBuybackTag, deleteBuybackTag, assignTagToProducts, removeTagFromProduct, getAllProductTags } from '@/services/api';
import { toast } from 'sonner';
import {
  RefreshCw, Loader2, Search, ChevronDown, ChevronUp, Filter,
  Globe, Eye, EyeOff, Package, Pencil, Scan, Check, X,
  Trophy, AlertTriangle, TrendingUp, ArrowRight, ArrowUp, Tag, Plus
} from 'lucide-react';

const GRADE_LABELS = {
  'FUNCTIONAL_FLAWLESS': { label: 'Parfait', short: 'A+', color: 'bg-emerald-500' },
  'FUNCTIONAL_GOOD': { label: 'Bon etat', short: 'A', color: 'bg-green-500' },
  'FUNCTIONAL_USED': { label: 'Correct', short: 'B', color: 'bg-blue-500' },
  'FUNCTIONAL_CRACKED': { label: 'Casse fonct.', short: 'C', color: 'bg-amber-500' },
  'NOT_FUNCTIONAL_USED': { label: 'HS correct', short: 'D', color: 'bg-orange-500' },
  'NOT_FUNCTIONAL_CRACKED': { label: 'HS casse', short: 'E', color: 'bg-red-500' },
};
const GRADE_ORDER = ['FUNCTIONAL_FLAWLESS', 'FUNCTIONAL_GOOD', 'FUNCTIONAL_USED', 'FUNCTIONAL_CRACKED', 'NOT_FUNCTIONAL_USED', 'NOT_FUNCTIONAL_CRACKED'];
const TAG_COLORS = ['#6366f1', '#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6'];

export default function BuyBackListingsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [filterBackbox, setFilterBackbox] = useState('all');
  const [filterMarket, setFilterMarket] = useState('all');
  const [filterTag, setFilterTag] = useState('all');
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [editingPrice, setEditingPrice] = useState(null);
  const [newPrice, setNewPrice] = useState('');
  const [updatingPrice, setUpdatingPrice] = useState(false);
  const editRef = useRef(null);
  const priceRef = useRef(null);
  // Tags
  const [tags, setTags] = useState([]);
  const [productTags, setProductTags] = useState({});
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#6366f1');
  const [showTagCreate, setShowTagCreate] = useState(false);
  // Mass selection
  const [selected, setSelected] = useState(new Set());
  const [massUpdating, setMassUpdating] = useState(false);
  const [massGrades, setMassGrades] = useState(new Set(GRADE_ORDER));
  const toggleMassGrade = (g) => { setMassGrades(prev => { const n = new Set(prev); n.has(g) ? n.delete(g) : n.add(g); return n; }); };

  const [forceLoading, setForceLoading] = useState(false);
  const [syncStep, setSyncStep] = useState('');  // '', 'listings', 'competitors', 'reload'

  const fetchListings = async (ignore, opts = {}) => {
    const { force = false } = opts;
    if (force) setForceLoading(true); else setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      // Force = full sync: 1) invalidate listings cache, 2) fetch listings, 3) sync competitors, 4) re-fetch
      if (force) {
        setSyncStep('listings');
        await fetch(`${apiBase}/api/buyback/listings/refresh`, {
          method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      // Phase 1: fetch listings (uses cached competitors if available)
      let [result, known, tagsRes, ptRes] = await Promise.all([
        getAllBuybackListings(), getKnownBuybackProducts(), getBuybackTags(), getAllProductTags()
      ]);
      if (!ignore) {
        setData(result);
        setSuggestions(known.filter(k => k.auto).map(k => k.name).sort());
        setTags(tagsRes.tags || []);
        setProductTags(ptRes.product_tags || {});
      }
      // Phase 2: full sync — refresh each active listing's BackBox individually (bypasses 60s timeout)
      if (force) {
        // Collect all active listing IDs
        const activeListingIds = [];
        for (const p of (result?.products || [])) {
          for (const [, gd] of Object.entries(p.grades || {})) {
            if (gd.active && gd.id) activeListingIds.push(gd.id);
          }
        }
        const total = activeListingIds.length;
        toast.info(`${total} annonces actives. Synchronisation BackBox en cours (~${Math.ceil(total * 0.6)}s)...`, { duration: 4000 });
        let okCount = 0, failCount = 0;
        for (let i = 0; i < activeListingIds.length; i++) {
          const lid = activeListingIds[i];
          setSyncStep(`competitors:${i+1}/${total}`);
          const r = await refreshSingleListingBB(lid);
          if (r) okCount++; else failCount++;
          // Gentle pacing to avoid BM rate limit
          if (i < activeListingIds.length - 1) await new Promise(res => setTimeout(res, 250));
        }
        if (failCount > 0) {
          toast.warning(`Sync partielle: ${okCount}/${total} BackBox récupérés (${failCount} échecs). Recliquez pour rattraper.`, { duration: 8000 });
        } else {
          toast.success(`Synchronisation Back Market complète: ${okCount} BackBox à jour`);
        }
      }
    } catch (err) { if (!ignore) toast.error(err.response?.data?.detail || err.message || 'Erreur chargement'); }
    finally { if (!ignore) { setLoading(false); setForceLoading(false); setSyncStep(''); } }
  };

  useEffect(() => { let ignore = false; fetchListings(ignore); return () => { ignore = true; }; }, []);

  const handleScanNames = async () => {
    setScanning(true);
    try { await scanBuybackProductNames(); toast.success('Scan en cours... Rechargez dans ~30s.'); }
    catch { toast.error('Erreur scan'); }
    finally { setScanning(false); }
  };

  const startEdit = (pid, name, e) => { e?.stopPropagation(); setEditingProduct(pid); setEditName(name || ''); setShowSuggestions(false); setTimeout(() => editRef.current?.focus(), 50); };
  const cancelEdit = (e) => { e?.stopPropagation(); setEditingProduct(null); setEditName(''); setShowSuggestions(false); };
  const handleSaveName = async (pid, e) => {
    e?.stopPropagation();
    if (!editName.trim()) return;
    setSaving(true);
    try {
      await setBuybackProductName(pid, editName.trim());
      setData(prev => ({ ...prev, products: prev.products.map(p => p.productId === pid ? { ...p, name: editName.trim() } : p) }));
      toast.success('Nom enregistre');
      setEditingProduct(null); setEditName(''); setShowSuggestions(false);
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const startPriceEdit = (listingId, grade, market, currentPrice, e) => {
    e?.stopPropagation();
    setEditingPrice({ listingId, grade, market });
    setNewPrice(String(currentPrice));
    setTimeout(() => priceRef.current?.focus(), 50);
  };
  const cancelPriceEdit = (e) => { e?.stopPropagation(); setEditingPrice(null); setNewPrice(''); };

  // After a price update, refresh competitor info for that listing only (fast, ~500ms)
  // Updates local state with the new BackBox status without triggering full reload
  const refreshSingleListingBB = async (listingId) => {
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/buyback/listings/${listingId}/refresh-competitor`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!r.ok) return null;
      const j = await r.json();
      // Update local state in place: find the grade with this listing id and update its backbox
      setData(prev => {
        if (!prev) return prev;
        const newProducts = prev.products.map(p => {
          let touched = false;
          const newGrades = {};
          for (const [g, gd] of Object.entries(p.grades || {})) {
            if (gd.id === listingId) {
              touched = true;
              newGrades[g] = { ...gd, backbox: j.backbox || {} };
            } else {
              newGrades[g] = gd;
            }
          }
          if (!touched) return p;
          // Recompute counts
          const bbCount = Object.values(newGrades).reduce((acc, g) => acc + Object.values(g.backbox || {}).filter(bb => bb.is_winning).length, 0);
          const noBbCount = Object.values(newGrades).reduce((acc, g) => acc + Object.values(g.backbox || {}).filter(bb => !bb.is_winning).length, 0);
          return { ...p, grades: newGrades, backbox_count: bbCount, no_backbox_count: noBbCount };
        });
        return { ...prev, products: newProducts };
      });
      return j;
    } catch { return null; }
  };

  const handlePriceUpdate = async (e) => {
    e?.stopPropagation();
    if (!editingPrice || !newPrice) return;
    const priceVal = parseFloat(newPrice);
    if (isNaN(priceVal) || priceVal < 0) { toast.error('Prix invalide'); return; }
    setUpdatingPrice(true);
    try {
      const payload = { prices: { [editingPrice.market]: { amount: priceVal.toFixed(2), currency: 'EUR' } } };
      await updateBuybackListing(editingPrice.listingId, payload);
      const lid = editingPrice.listingId;
      setEditingPrice(null); setNewPrice('');
      // Targeted BackBox refresh for this listing only
      await refreshSingleListingBB(lid);
      toast.success(`Prix ${editingPrice?.market || ''} mis a jour: ${priceVal.toFixed(2)} EUR (BackBox actualise)`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur mise a jour prix'); }
    finally { setUpdatingPrice(false); }
  };

  // Quick validate: apply suggested price immediately
  const quickApply = async (listingId, grade, market, price, e) => {
    e?.stopPropagation();
    setUpdatingPrice(true);
    try {
      const payload = { prices: { [market]: { amount: price.toFixed(2), currency: 'EUR' } } };
      await updateBuybackListing(listingId, payload);
      // Targeted BackBox refresh for this listing
      await refreshSingleListingBB(listingId);
      toast.success(`${market} ajuste a ${price.toFixed(2)} EUR (BackBox actualise)`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setUpdatingPrice(false); }
  };

  // Refresh BackBox for multiple listings sequentially (after mass actions)
  const refreshMultipleListingsBB = async (listingIds) => {
    const unique = [...new Set(listingIds)];
    let ok = 0;
    for (const lid of unique) {
      const r = await refreshSingleListingBB(lid);
      if (r) ok++;
      await new Promise(res => setTimeout(res, 200));  // gentle pacing
    }
    return { total: unique.length, ok };
  };

  // Mass update: apply suggested or +N to selected products on selected grades
  const massApply = async (offset = 0) => {
    if (selected.size === 0) { toast.error('Aucun produit selectionne'); return; }
    if (massGrades.size === 0) { toast.error('Selectionnez au moins un grade'); return; }
    setMassUpdating(true);
    let success = 0, errors = 0;
    const touchedListings = [];
    const selectedProducts = filteredProducts.filter(p => selected.has(p.productId));
    for (const product of selectedProducts) {
      for (const g of GRADE_ORDER) {
        if (!massGrades.has(g)) continue;
        const gd = product.grades[g];
        if (!gd) continue;
        const bbEntries = Object.entries(gd.backbox || {});
        for (const [market, bb] of bbEntries) {
          if (bb.is_winning) continue;
          const newP = bb.price_to_win + offset;
          try {
            await updateBuybackListing(gd.id, { prices: { [market]: { amount: newP.toFixed(2), currency: 'EUR' } } });
            success++;
            touchedListings.push(gd.id);
          } catch { errors++; }
        }
      }
    }
    toast.success(`${success} prix mis a jour${errors > 0 ? `, ${errors} erreurs` : ''}. Actualisation BackBox...`);
    if (touchedListings.length > 0) {
      const r = await refreshMultipleListingsBB(touchedListings);
      toast.success(`BackBox actualise: ${r.ok}/${r.total} listings`);
    }
    setMassUpdating(false);
    setSelected(new Set());
  };

  // Mass set fixed price on selected products/grades
  const massSetFixed = async (fixedPrice) => {
    if (selected.size === 0) { toast.error('Aucun produit selectionne'); return; }
    if (massGrades.size === 0) { toast.error('Selectionnez au moins un grade'); return; }
    setMassUpdating(true);
    let success = 0, errors = 0;
    const touchedListings = [];
    const selectedProducts = filteredProducts.filter(p => selected.has(p.productId));
    for (const product of selectedProducts) {
      for (const g of GRADE_ORDER) {
        if (!massGrades.has(g)) continue;
        const gd = product.grades[g];
        if (!gd) continue;
        const bbEntries = Object.entries(gd.backbox || {});
        for (const [market] of bbEntries) {
          try {
            await updateBuybackListing(gd.id, { prices: { [market]: { amount: fixedPrice.toFixed(2), currency: 'EUR' } } });
            success++;
            touchedListings.push(gd.id);
          } catch { errors++; }
        }
        // If no backbox entries, still try to update with known markets
        if (bbEntries.length === 0 && gd.id) {
          const markets = [];
          if (gd.fr_price !== undefined) markets.push('FR');
          if (gd.de_price !== undefined) markets.push('DE');
          for (const market of markets) {
            try {
              await updateBuybackListing(gd.id, { prices: { [market]: { amount: fixedPrice.toFixed(2), currency: 'EUR' } } });
              success++;
              touchedListings.push(gd.id);
            } catch { errors++; }
          }
        }
      }
    }
    toast.success(`${success} prix mis a ${fixedPrice} EUR${errors > 0 ? `, ${errors} erreurs` : ''}. Actualisation BackBox...`);
    if (touchedListings.length > 0) {
      const r = await refreshMultipleListingsBB(touchedListings);
      toast.success(`BackBox actualise: ${r.ok}/${r.total} listings`);
    }
    setMassUpdating(false);
    setSelected(new Set());
  };

  // Tags
  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    try {
      const res = await createBuybackTag(newTagName.trim(), newTagColor);
      setTags(prev => [...prev, { id: res.id, name: newTagName.trim(), color: newTagColor }]);
      setNewTagName(''); setShowTagCreate(false);
      toast.success(`Balise "${newTagName.trim()}" creee`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await deleteBuybackTag(tagId);
      setTags(prev => prev.filter(t => t.id !== tagId));
      setProductTags(prev => {
        const n = { ...prev };
        for (const pid in n) n[pid] = n[pid].filter(t => t !== tagId);
        return n;
      });
      if (filterTag === tagId) setFilterTag('all');
      toast.success('Balise supprimee');
    } catch { toast.error('Erreur'); }
  };

  const handleAssignTag = async (tagId) => {
    if (selected.size === 0) { toast.error('Selectionnez des produits'); return; }
    try {
      await assignTagToProducts(tagId, [...selected]);
      setProductTags(prev => {
        const n = { ...prev };
        for (const pid of selected) {
          if (!n[pid]) n[pid] = [];
          if (!n[pid].includes(tagId)) n[pid].push(tagId);
        }
        return n;
      });
      toast.success(`Balise ajoutee a ${selected.size} produits`);
    } catch { toast.error('Erreur'); }
  };

  const handleRemoveTag = async (productId, tagId, e) => {
    e?.stopPropagation();
    try {
      await removeTagFromProduct(productId, tagId);
      setProductTags(prev => ({ ...prev, [productId]: (prev[productId] || []).filter(t => t !== tagId) }));
    } catch { toast.error('Erreur'); }
  };

  const toggleSelect = (pid, e) => { e?.stopPropagation(); setSelected(prev => { const n = new Set(prev); n.has(pid) ? n.delete(pid) : n.add(pid); return n; }); };
  const selectAll = () => {
    const ids = new Set(filteredProducts.map(p => p.productId));
    setSelected(prev => prev.size === ids.size && [...ids].every(id => prev.has(id)) ? new Set() : ids);
  };

  const filteredSuggestions = useMemo(() => {
    if (!editName) return suggestions;
    const q = editName.toLowerCase();
    return suggestions.filter(s => s.toLowerCase().includes(q));
  }, [suggestions, editName]);

  const filteredProducts = useMemo(() => {
    if (!data) return [];
    let products = data.products;
    if (filterActive === 'active') products = products.filter(p => p.active_grades > 0);
    else if (filterActive === 'inactive') products = products.filter(p => p.active_grades === 0);
    if (filterBackbox === 'has') products = products.filter(p => p.backbox_count > 0);
    else if (filterBackbox === 'missing') products = products.filter(p => p.no_backbox_count > 0);
    if (filterMarket !== 'all') products = products.filter(p => p.active_markets.includes(filterMarket));
    if (filterTag !== 'all') products = products.filter(p => (productTags[p.productId] || []).includes(filterTag));
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      products = products.filter(p => (p.name && p.name.toLowerCase().includes(q)) || p.productId.toLowerCase().includes(q));
    }
    return products;
  }, [data, filterActive, filterBackbox, filterMarket, filterTag, searchQuery, productTags]);

  const stats = useMemo(() => {
    if (!data) return { total: 0, active: 0, products: 0, bb: 0, nobb: 0 };
    const bb = data.products.reduce((acc, p) => acc + p.backbox_count, 0);
    const nobb = data.products.reduce((acc, p) => acc + p.no_backbox_count, 0);
    return { total: data.total_listings, active: data.active_listings, products: data.total_products, bb, nobb };
  }, [data]);

  const isAllSelected = filteredProducts.length > 0 && filteredProducts.every(p => selected.has(p.productId));

  if (loading && !data) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /><span className="ml-3 text-slate-500">Chargement...</span></div>;
  }

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Annonces', value: stats.total, icon: Package, color: 'bg-slate-100 text-slate-700' },
          { label: 'Actives', value: stats.active, icon: Eye, color: 'bg-blue-50 text-blue-700' },
          { label: 'BackBox', value: stats.bb, icon: Trophy, color: 'bg-emerald-50 text-emerald-700' },
          { label: 'Pas BackBox', value: stats.nobb, icon: AlertTriangle, color: stats.nobb > 0 ? 'bg-red-50 text-red-700' : 'bg-slate-50 text-slate-500' },
          { label: 'Produits', value: stats.products, icon: Package, color: 'bg-indigo-50 text-indigo-700' },
        ].map((s, i) => (
          <Card key={i} className={`border-0 ${s.color}`}>
            <CardContent className="py-2.5 px-3 flex items-center gap-2">
              <s.icon className="w-4 h-4 opacity-60" /><div><p className="text-xl font-bold">{s.value}</p><p className="text-xs opacity-60">{s.label}</p></div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="pt-3 pb-3">
          <div className="flex flex-wrap items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <div className="relative flex-1 min-w-[130px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Rechercher..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 h-9" data-testid="search-listings-input" />
            </div>
            <Select value={filterActive} onValueChange={setFilterActive}>
              <SelectTrigger className="w-[110px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Statut</SelectItem><SelectItem value="active">Actives</SelectItem><SelectItem value="inactive">Inactives</SelectItem></SelectContent>
            </Select>
            <Select value={filterBackbox} onValueChange={setFilterBackbox}>
              <SelectTrigger className="w-[130px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">BackBox</SelectItem><SelectItem value="has">Avec BB</SelectItem><SelectItem value="missing">Sans BB</SelectItem></SelectContent>
            </Select>
            <Select value={filterMarket} onValueChange={setFilterMarket}>
              <SelectTrigger className="w-[90px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Marche</SelectItem><SelectItem value="FR">FR</SelectItem><SelectItem value="DE">DE</SelectItem></SelectContent>
            </Select>
            {/* Tag filter */}
            <Select value={filterTag} onValueChange={setFilterTag}>
              <SelectTrigger className="w-[130px] h-9"><SelectValue placeholder="Balise" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes balises</SelectItem>
                {tags.map(t => <SelectItem key={t.id} value={t.id}><span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: t.color }}></span>{t.name}</span></SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" className="h-9 text-xs gap-1" onClick={handleScanNames} disabled={scanning} data-testid="scan-names-btn">
              {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Scan className="w-3.5 h-3.5" />}Scanner
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-9"
              onClick={() => fetchListings(false)}
              disabled={loading || forceLoading}
              title="Recharger (cache 15 min)"
              data-testid="reload-listings-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="default"
              size="sm"
              className="h-9 gap-1.5 bg-orange-600 hover:bg-orange-700 text-white"
              onClick={() => fetchListings(false, { force: true })}
              disabled={loading || forceLoading}
              title="Forcer un appel API Back Market complet (annonces + BackBox, ~30s)"
              data-testid="force-refresh-bm-btn"
            >
              {forceLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              <span className="text-xs">
                {syncStep === 'listings' && 'Annonces...'}
                {syncStep && syncStep.startsWith('competitors:') && `BackBox ${syncStep.split(':')[1]}`}
                {syncStep === 'reload' && 'Rechargement...'}
                {!syncStep && 'Sync BM'}
              </span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tags management + Mass actions */}
      <Card className="border-slate-200 bg-slate-50/50">
        <CardContent className="py-2 px-3">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Tags */}
            <Tag className="w-3.5 h-3.5 text-slate-400" />
            {tags.map(t => (
              <div key={t.id} className="flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-medium text-white" style={{ backgroundColor: t.color }}>
                {t.name}
                <button onClick={() => handleDeleteTag(t.id)} className="ml-0.5 hover:opacity-70"><X className="w-2.5 h-2.5" /></button>
              </div>
            ))}
            {showTagCreate ? (
              <div className="flex items-center gap-1">
                <Input value={newTagName} onChange={e => setNewTagName(e.target.value)} placeholder="Nom..." className="h-6 w-24 text-xs"
                  onKeyDown={e => { if (e.key === 'Enter') handleCreateTag(); if (e.key === 'Escape') setShowTagCreate(false); }} />
                <div className="flex gap-0.5">
                  {TAG_COLORS.map(c => <button key={c} onClick={() => setNewTagColor(c)} className={`w-4 h-4 rounded-full border-2 ${newTagColor === c ? 'border-slate-800' : 'border-transparent'}`} style={{ backgroundColor: c }} />)}
                </div>
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={handleCreateTag}><Check className="w-3 h-3" /></Button>
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setShowTagCreate(false)}><X className="w-3 h-3" /></Button>
              </div>
            ) : (
              <button onClick={() => setShowTagCreate(true)} className="flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 text-[10px] hover:bg-slate-300">
                <Plus className="w-2.5 h-2.5" />Balise
              </button>
            )}
            {/* Mass actions */}
            {selected.size > 0 && (
              <>
                <span className="text-slate-300 mx-1">|</span>
                <span className="text-xs font-semibold text-slate-700">{selected.size} select.</span>
                {tags.length > 0 && (
                  <Select onValueChange={handleAssignTag}>
                    <SelectTrigger className="h-6 w-[120px] text-[10px]"><SelectValue placeholder="Assigner balise" /></SelectTrigger>
                    <SelectContent>{tags.map(t => <SelectItem key={t.id} value={t.id}><span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: t.color }}></span>{t.name}</span></SelectItem>)}</SelectContent>
                  </Select>
                )}
                <span className="text-slate-300 mx-1">|</span>
                <div className="flex items-center gap-0.5">
                  {GRADE_ORDER.map(g => {
                    const cfg = GRADE_LABELS[g];
                    return (
                      <button key={g} onClick={() => toggleMassGrade(g)}
                        className={`px-1.5 py-0.5 rounded text-[9px] font-semibold transition-colors ${massGrades.has(g) ? `text-white ${cfg.color}` : 'bg-slate-100 text-slate-400'}`}>
                        {cfg.short}
                      </button>
                    );
                  })}
                </div>
                <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1 text-emerald-700 border-emerald-300" onClick={() => massApply(0)} disabled={massUpdating || massGrades.size === 0}>
                  {massUpdating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}Suggere
                </Button>
                <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1 text-amber-700 border-amber-300" onClick={() => massApply(1)} disabled={massUpdating || massGrades.size === 0}>
                  <ArrowUp className="w-3 h-3" />+1
                </Button>
                <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1 text-amber-700 border-amber-300" onClick={() => massApply(2)} disabled={massUpdating || massGrades.size === 0}>
                  <ArrowUp className="w-3 h-3" />+2
                </Button>
                <span className="text-slate-300 mx-1">|</span>
                <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1 text-red-600 border-red-300" onClick={() => massSetFixed(0)} disabled={massUpdating || massGrades.size === 0}>
                  <X className="w-3 h-3" />0 EUR
                </Button>
                <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1 text-slate-600 border-slate-300" onClick={() => massSetFixed(5)} disabled={massUpdating || massGrades.size === 0}>
                  5 EUR
                </Button>
                <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1 text-slate-600 border-slate-300" onClick={() => massSetFixed(10)} disabled={massUpdating || massGrades.size === 0}>
                  10 EUR
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Select all */}
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" checked={isAllSelected} onChange={selectAll} className="rounded" />
          Tout ({filteredProducts.length})
        </label>
        <span className="text-xs text-slate-400">{filteredProducts.length} produits</span>
      </div>

      {/* Products */}
      {filteredProducts.map(product => {
        const isExpanded = expandedProduct === product.productId;
        const isEditing = editingProduct === product.productId;
        const isActive = product.active_grades > 0;
        const isSelected = selected.has(product.productId);
        const pTags = (productTags[product.productId] || []).map(tid => tags.find(t => t.id === tid)).filter(Boolean);
        return (
          <Card key={product.productId} className={`border-slate-200 ${isSelected ? 'ring-2 ring-indigo-300 bg-indigo-50/20' : ''} ${isActive ? '' : 'opacity-50'}`} data-testid={`product-card-${product.productId.slice(0,8)}`}>
            <CardHeader className="pb-2 cursor-pointer" onClick={() => setExpandedProduct(isExpanded ? null : product.productId)}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <input type="checkbox" checked={isSelected} onChange={e => toggleSelect(product.productId, e)} onClick={e => e.stopPropagation()} className="rounded" />
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  {isEditing ? (
                    <div className="relative" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <Input ref={editRef} value={editName} onChange={e => { setEditName(e.target.value); setShowSuggestions(true); }}
                          onKeyDown={e => { if (e.key === 'Enter') handleSaveName(product.productId, e); if (e.key === 'Escape') cancelEdit(e); }}
                          className="h-7 text-sm w-[260px]" data-testid="inline-name-input" />
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-emerald-600" onClick={e => handleSaveName(product.productId, e)} disabled={saving || !editName.trim()}>
                          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400" onClick={cancelEdit}><X className="w-3.5 h-3.5" /></Button>
                      </div>
                      {showSuggestions && filteredSuggestions.length > 0 && (
                        <div className="absolute z-50 top-8 left-0 bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto w-[280px]">
                          {filteredSuggestions.slice(0, 10).map(s => (
                            <button key={s} onClick={e => { e.stopPropagation(); setEditName(s); setShowSuggestions(false); }}
                              className="w-full text-left px-3 py-1.5 text-xs hover:bg-emerald-50 border-b border-slate-50 last:border-0">{s}</button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {product.name || <span className="text-slate-400 font-mono text-sm">{product.productId.slice(0, 16)}...</span>}
                      </CardTitle>
                      <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-slate-300 hover:text-emerald-600" onClick={e => startEdit(product.productId, product.name, e)}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      {pTags.map(t => (
                        <span key={t.id} className="flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[9px] font-medium text-white" style={{ backgroundColor: t.color }}>
                          {t.name}
                          <button onClick={e => handleRemoveTag(product.productId, t.id, e)} className="hover:opacity-70"><X className="w-2 h-2" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {product.active_markets.map(m => <Badge key={m} className="bg-indigo-100 text-indigo-600 border-0 text-[10px]">{m}</Badge>)}
                  {product.backbox_count > 0 && <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs gap-1"><Trophy className="w-3 h-3" />{product.backbox_count}</Badge>}
                  {product.no_backbox_count > 0 && <Badge className="bg-red-100 text-red-700 border-0 text-xs gap-1"><AlertTriangle className="w-3 h-3" />{product.no_backbox_count}</Badge>}
                  {!isActive && <Badge className="bg-slate-100 text-slate-500 border-0 text-[10px]"><EyeOff className="w-3 h-3 mr-1" />Hors ligne</Badge>}
                </div>
              </div>
              <p className="text-[10px] text-slate-400 ml-12 mt-0.5 font-mono">{product.productId}</p>
            </CardHeader>
            {isExpanded && (
              <CardContent className="pt-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50/80">
                        <th className="py-2 px-3 text-left text-xs font-medium text-slate-500">Grade</th>
                        <th className="py-2 px-3 text-center text-xs font-medium text-slate-500">Marche</th>
                        <th className="py-2 px-3 text-right text-xs font-medium text-slate-500">Mon prix</th>
                        <th className="py-2 px-3 text-right text-xs font-medium text-slate-500">Prix pour BB</th>
                        <th className="py-2 px-3 text-right text-xs font-medium text-slate-500">Ecart</th>
                        <th className="py-2 px-3 text-center text-xs font-medium text-slate-500">Statut</th>
                        <th className="py-2 px-3 text-center text-xs font-medium text-slate-500">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {GRADE_ORDER.map(g => {
                        const gd = product.grades[g];
                        if (!gd) return null;
                        const cfg = GRADE_LABELS[g];
                        const bbEntries = Object.entries(gd.backbox || {});
                        if (bbEntries.length === 0) {
                          return (
                            <tr key={g} className="border-b border-slate-100">
                              <td className="py-2 px-3"><div className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${cfg.color}`}></span><span className="text-xs">{cfg.short} - {cfg.label}</span></div></td>
                              <td className="py-2 px-3 text-center text-[10px] text-slate-400">-</td>
                              <td className="py-2 px-3 text-right text-xs text-slate-400">{gd.fr_price > 0 ? `${gd.fr_price.toFixed(0)} EUR` : gd.de_price > 0 ? `${gd.de_price.toFixed(0)} EUR` : '-'}</td>
                              <td className="py-2 px-3 text-right text-xs text-slate-400">-</td>
                              <td className="py-2 px-3 text-right text-xs text-slate-400">-</td>
                              <td className="py-2 px-3 text-center"><Badge className="bg-slate-100 text-slate-400 border-0 text-[10px]">Hors ligne</Badge></td>
                              <td className="py-2 px-3 text-center text-xs text-slate-300">-</td>
                            </tr>
                          );
                        }
                        return bbEntries.map(([market, bb]) => {
                          const diff = bb.my_price - bb.price_to_win;
                          const gap = Math.abs(diff).toFixed(2);
                          const isPriceEditing = editingPrice?.listingId === gd.id && editingPrice?.grade === g && editingPrice?.market === market;
                          return (
                            <tr key={`${g}_${market}`} className={`border-b border-slate-100 ${bb.is_winning ? 'bg-emerald-50/30' : 'bg-red-50/20'}`}>
                              <td className="py-2 px-3"><div className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${cfg.color}`}></span><span className="text-xs">{cfg.short} - {cfg.label}</span></div></td>
                              <td className="py-2 px-3 text-center"><Badge className={`border-0 text-[10px] ${market === 'FR' ? 'bg-indigo-100 text-indigo-600' : 'bg-amber-100 text-amber-600'}`}>{market}</Badge></td>
                              <td className="py-2 px-3 text-right" onClick={e => e.stopPropagation()}>
                                {isPriceEditing ? (
                                  <div className="flex items-center justify-end gap-1">
                                    <Input ref={priceRef} type="number" step="0.01" min="0" value={newPrice}
                                      onChange={e => setNewPrice(e.target.value)}
                                      onKeyDown={e => { if (e.key === 'Enter') handlePriceUpdate(e); if (e.key === 'Escape') cancelPriceEdit(e); }}
                                      className="h-7 w-20 text-sm text-right" data-testid="price-edit-input" />
                                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-emerald-600" onClick={handlePriceUpdate} disabled={updatingPrice} data-testid="price-save-btn">
                                      {updatingPrice ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                                    </Button>
                                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400" onClick={cancelPriceEdit}><X className="w-3.5 h-3.5" /></Button>
                                  </div>
                                ) : (
                                  <span className="font-semibold text-sm cursor-pointer hover:text-emerald-600 transition-colors" onClick={e => startPriceEdit(gd.id, g, market, bb.my_price, e)} data-testid={`price-display-${g}-${market}`}>
                                    {bb.my_price.toFixed(2)} {bb.currency}
                                  </span>
                                )}
                              </td>
                              <td className="py-2 px-3 text-right text-xs text-slate-600">{bb.price_to_win.toFixed(2)} {bb.currency}</td>
                              <td className="py-2 px-3 text-right">
                                {bb.is_winning ? (
                                  <span className="text-emerald-600 text-xs font-medium">= 0</span>
                                ) : (
                                  <span className="text-red-600 text-xs font-medium flex items-center justify-end gap-1">
                                    <TrendingUp className="w-3 h-3" />+{gap} {bb.currency}
                                  </span>
                                )}
                              </td>
                              <td className="py-2 px-3 text-center">
                                {bb.is_winning ? (
                                  <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px] gap-1" data-testid={`bb-win-${g}-${market}`}>
                                    <Trophy className="w-3 h-3" />BackBox
                                  </Badge>
                                ) : (
                                  <div className="flex flex-col items-center gap-0.5">
                                    <Badge className="bg-red-100 text-red-700 border-0 text-[10px] gap-1" data-testid={`bb-lose-${g}-${market}`}>
                                      <AlertTriangle className="w-3 h-3" />Pas BB
                                    </Badge>
                                    <span className="text-[9px] text-red-500">
                                      <ArrowRight className="w-2.5 h-2.5 inline" /> {bb.price_to_win.toFixed(2)} min.
                                    </span>
                                  </div>
                                )}
                              </td>
                              <td className="py-2 px-3 text-center" onClick={e => e.stopPropagation()}>
                                {!bb.is_winning && (
                                  <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                                    onClick={e => quickApply(gd.id, g, market, bb.price_to_win, e)} disabled={updatingPrice}
                                    data-testid={`win-bb-btn-${g}-${market}`}>
                                    <ArrowUp className="w-3 h-3" />Ajuster
                                  </Button>
                                )}
                              </td>
                            </tr>
                          );
                        });
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            )}
          </Card>
        );
      })}
      {filteredProducts.length === 0 && !loading && <div className="text-center py-12 text-slate-500">Aucun produit trouve</div>}
    </div>
  );
}
